// Logística (docs/110): contratos y lecturas de la Orden de Despacho y sus maestros. Toda clave cuelga
// de ['emp', e, 'logistica', …]: cualquier acción de la OD invalida `logistica(e)` (y el stock).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/cliente';
import { useIntentos } from '../api/idempotencia';

export type EstadoOd =
  | 'PLANIFICADA'
  | 'EN_PREPARACION'
  | 'VALIDADA'
  | 'CARGADA'
  | 'APROBADA'
  | 'FACTURADA'
  | 'EN_RUTA'
  | 'RENDIDA'
  | 'CERRADA'
  | 'ANULADA';

export const NOMBRE_ESTADO_OD: Record<EstadoOd, string> = {
  PLANIFICADA: 'Creada',
  EN_PREPARACION: 'En preparación',
  VALIDADA: 'Carga validada',
  CARGADA: 'Cargada',
  APROBADA: 'Aprobada para facturar',
  FACTURADA: 'Documentos emitidos',
  EN_RUTA: 'En ruta',
  RENDIDA: 'Rendida',
  CERRADA: 'Cerrada',
  ANULADA: 'Anulada',
};
export const PASOS_OD: EstadoOd[] = [
  'PLANIFICADA',
  'EN_PREPARACION',
  'VALIDADA',
  'CARGADA',
  'APROBADA',
  'FACTURADA',
  'EN_RUTA',
  'RENDIDA',
  'CERRADA',
];
/**
 * OD simple (pedido de Rodrigo, 2026-10-13): crear → manifiesto (el encargado carga) → descontar
 * faltantes → documentos y hoja de rendición → salida (el camión se carga en el stock) → rendición.
 * Las OD que ya iban por el camino largo (preparación, validación, aprobación) lo terminan igual.
 */
export const PASOS_OD_SIMPLE: EstadoOd[] = [
  'PLANIFICADA',
  'FACTURADA',
  'EN_RUTA',
  'RENDIDA',
  'CERRADA',
];
export const CAMINO_LARGO: EstadoOd[] = ['EN_PREPARACION', 'VALIDADA', 'CARGADA', 'APROBADA'];

export interface NotaDisponible {
  notaId: string;
  numeroVisible: string;
  fechaNegocio: string;
  clienteEntidadId: string;
  clienteRazonSocial: string;
  clienteRut: string | null;
  direccionEntrega: string | null;
  zonaId: string | null;
  zonaNombre: string | null;
  vendedorId: string | null;
  condicionPago: string;
  documentoSolicitado: string;
  canal: string;
  revision: number;
  lineasPendientes: number;
  unidadesPendientes: string;
  /** Día comprometido de entrega (la fecha de la nota si no se indicó otra). */
  fechaEntrega?: string;
  /** Kilos pendientes de la nota (peso del catálogo × lo pendiente). */
  pesoKg?: string;
  /** Productos de la nota sin peso en el catálogo (sus kilos no se cuentan). */
  productosSinPeso?: number;
  bruto: string | null;
  enOd: number | null;
  /** N° de la ODD donde el cliente rechazó y falta la NC: la nota no vuelve a salir (2026-10-31). */
  esperaNc?: number | null;
}

export interface OdListada {
  id: string;
  numero: number;
  estado: EstadoOd;
  fechaNegocio: string;
  version: number;
  nombre?: string | null;
  patente: string | null;
  chofer: string | null;
  recaudador?: string | null;
  ruta: string | null;
  notas: number;
}

export interface LineaOd {
  notaLineaId: string;
  lineaNumero: number;
  productoId: string;
  sku: string;
  descripcion: string;
  planificado: string;
  preparado: string | null;
  cargado: string | null;
  entregado: string | null;
  rechazado: string | null;
  motivoRechazo: string | null;
}

export interface NotaOd {
  notaId: string;
  parada: number;
  estado: string;
  activa: boolean;
  numeroVisible: string;
  clienteEntidadId: string;
  cliente: string;
  clienteRut: string | null;
  direccionEntrega: string | null;
  zona: string | null;
  vendedorId: string | null;
  condicionPago: string;
  /** 0107: el cliente paga lo pendiente al recibir (hoja de cobranza). */
  cobroContraEntrega?: boolean;
  documentoSolicitado: string;
  documentacionId: string | null;
  despachoId: string | null;
  requiereNc: boolean;
  incidencia: string | null;
  motivo: string | null;
  lineas: LineaOd[];
  cobros: Array<{
    cobroId: string;
    medio: string;
    monto: string;
    referencia: string | null;
    registradoEn: string;
    /** El recibo de caja que lo imputó a la cuenta del cliente (null: falta imputar). */
    reciboId: string | null;
    /** docs/111 · APP = el peoneta en el celular; MANUAL = la oficina. */
    origen: 'APP' | 'MANUAL';
    banco: string | null;
    numeroDocumento: string | null;
    fechaDocumento: string | null;
    anulado: boolean;
    tieneFoto: boolean;
    /** Estado en tesorería (null: todavía no lo recibe). */
    estadoTesoreria: 'RECIBIDO' | 'NO_LLEGO' | 'POR_VERIFICAR' | 'VERIFICADA' | 'RECHAZADA' | null;
  }>;
}

export interface Od {
  id: string;
  numero: number;
  estado: EstadoOd;
  version: number;
  modoOperacion: string;
  fechaNegocio: string;
  bodegaId: string;
  vehiculoId: string | null;
  patente: string | null;
  vehiculoDescripcion: string | null;
  camionUbicacionId: string | null;
  choferId: string | null;
  chofer: string | null;
  choferRut: string | null;
  nombre?: string | null;
  recaudadorId?: string | null;
  recaudador?: string | null;
  recaudadorTelefono?: string | null;
  ayudantes: string[];
  rutaId: string | null;
  ruta: string | null;
  observaciones: string | null;
  trasladoCargaId: string | null;
  trasladoDescargaId: string | null;
  aprobadaEn: string | null;
  salidaEn: string | null;
  creadaEn: string;
  notas: NotaOd[];
  pasos: Array<{
    paso: string;
    notaId: string | null;
    estado: string;
    resultadoId: string | null;
    detalle?: Record<string, unknown>;
    error: string | null;
    registradoEn: string;
  }>;
  historia: Array<{
    desde: string | null;
    hasta: string;
    usuarioId: string | null;
    motivo: string | null;
    registradoEn: string;
  }>;
}

export interface ProductoManifiesto {
  productoId: string;
  sku: string;
  descripcion: string;
  total: string;
  /** Raíz de la categoría del producto (como «Super Familia» en el manifiesto de Random). */
  superFamilia?: { codigo: string; nombre: string } | null;
  unidadBase?: string;
  /** La caja: la presentación vendible más chica sobre la base (unidad y unidades que trae). */
  caja?: { unidad: string; factor: string } | null;
  kilos?: string | null;
  detalle: Array<{
    notaId: string;
    numeroVisible: string;
    cliente: string;
    parada: number;
    cantidad: string;
  }>;
}

export interface DiferenciaCarga {
  notaId?: string;
  numeroVisible?: string;
  cliente?: string;
  lineaNumero?: number;
  productoId?: string;
  sku?: string;
  descripcion?: string;
  esperado?: string;
  preparado?: string;
  faltante?: string;
  problema: string;
}

export interface DocumentosNotaOd {
  notaId: string;
  numeroVisible: string;
  cliente: string;
  documentoSolicitado: string;
  incidencia: string | null;
  documentos: Array<{
    documentacionId: string;
    documentoId: string | null;
    tipoDte: number | null;
    folio: string | null;
    estado: string | null;
    trackId: string | null;
    monto: string | null;
  }>;
}

export interface Vehiculo {
  id: string;
  patente: string;
  descripcion: string;
  ubicacionId: string;
  capacidadKg: string | null;
  activo: boolean;
}
export interface Chofer {
  id: string;
  rut: string;
  dv: string;
  nombre: string;
  telefono: string | null;
  licencia: string | null;
  activo: boolean;
  /** CHOFER = vehículo y carga; PEONETA_RECAUDADOR = ruta y cobranza (0098). */
  rol?: RolPersonalRuta;
  /** docs/111 · usuario del ERP con que entra a la app del peoneta (#/ruta); null = rinde a mano. */
  usuarioId?: string | null;
}
export type RolPersonalRuta = 'CHOFER' | 'PEONETA_RECAUDADOR' | 'AMBOS';
export const NOMBRE_ROL_PERSONAL: Record<RolPersonalRuta, string> = {
  CHOFER: 'Chofer',
  PEONETA_RECAUDADOR: 'Peoneta recaudador',
  AMBOS: 'Chofer y recaudador',
};
export interface Ruta {
  id: string;
  codigo: string;
  nombre: string;
  activo: boolean;
  zonaIds: string[];
}

export const clavesLog = {
  logistica: (e: string) => ['emp', e, 'logistica'] as const,
  ordenes: (e: string, f: object) => ['emp', e, 'logistica', 'ordenes', f] as const,
  od: (e: string, id: string) => ['emp', e, 'logistica', 'od', id] as const,
  manifiesto: (e: string, id: string) => ['emp', e, 'logistica', 'od', id, 'manifiesto'] as const,
  revision: (e: string, id: string) => ['emp', e, 'logistica', 'od', id, 'revision'] as const,
  documentos: (e: string, id: string) => ['emp', e, 'logistica', 'od', id, 'documentos'] as const,
  disponibles: (e: string, f: object) => ['emp', e, 'logistica', 'disponibles', f] as const,
  vehiculos: (e: string) => ['emp', e, 'logistica', 'vehiculos'] as const,
  choferes: (e: string) => ['emp', e, 'logistica', 'choferes'] as const,
  rutas: (e: string) => ['emp', e, 'logistica', 'rutas'] as const,
};

export function useOrdenes(
  empresaId: string,
  f: { estado?: string; desde?: string; hasta?: string },
) {
  return useQuery({
    queryKey: clavesLog.ordenes(empresaId, f),
    queryFn: () =>
      api.get<OdListada[]>('/logistica/ordenes', {
        query: { empresaId, estado: f.estado, desde: f.desde, hasta: f.hasta },
      }),
  });
}

export function useOd(empresaId: string, odId: string | null) {
  return useQuery({
    queryKey: clavesLog.od(empresaId, odId ?? ''),
    queryFn: () => api.get<Od>(`/logistica/ordenes/${odId}`, { query: { empresaId } }),
    enabled: !!odId,
  });
}

export function useManifiesto(empresaId: string, odId: string | null, habilitado = true) {
  return useQuery({
    queryKey: clavesLog.manifiesto(empresaId, odId ?? ''),
    queryFn: () =>
      api.get<{
        productos: ProductoManifiesto[];
        resumen?: { kilos: string; notas: number; clientes: number };
      }>(`/logistica/ordenes/${odId}/manifiesto`, {
        query: { empresaId },
      }),
    enabled: !!odId && habilitado,
  });
}

export function useRevisionCarga(empresaId: string, odId: string | null, habilitado: boolean) {
  return useQuery({
    queryKey: clavesLog.revision(empresaId, odId ?? ''),
    queryFn: () =>
      api.get<DiferenciaCarga[]>(`/logistica/ordenes/${odId}/revision-carga`, {
        query: { empresaId },
      }),
    enabled: !!odId && habilitado,
  });
}

export function useDocumentosOd(empresaId: string, odId: string | null, habilitado: boolean) {
  return useQuery({
    queryKey: clavesLog.documentos(empresaId, odId ?? ''),
    queryFn: () =>
      api.get<DocumentosNotaOd[]>(`/logistica/ordenes/${odId}/documentos`, {
        query: { empresaId },
      }),
    enabled: !!odId && habilitado,
  });
}

export interface FiltroPlanificador {
  ubicacionId: string;
  desde?: string | undefined;
  hasta?: string | undefined;
  zonaId?: string | undefined;
  comuna?: string | undefined;
  vendedorId?: string | undefined;
  condicionPago?: string | undefined;
  entregaDesde?: string | undefined;
  entregaHasta?: string | undefined;
  rutaId?: string | undefined;
  /** SKU exacto o parte de la descripción de un producto pendiente. */
  producto?: string | undefined;
}

/** `cada`: refresco automático en ms (la app de bodega ve sola lo que otros toman en otra ODD). */
export function useDisponibles(empresaId: string, f: FiltroPlanificador, cada?: number) {
  return useQuery({
    queryKey: clavesLog.disponibles(empresaId, f),
    queryFn: () =>
      api.get<NotaDisponible[]>('/logistica/disponibles', { query: { empresaId, ...f } }),
    enabled: !!f.ubicacionId,
    ...(cada ? { refetchInterval: cada } : {}),
  });
}

export const useVehiculos = (empresaId: string) =>
  useQuery({
    queryKey: clavesLog.vehiculos(empresaId),
    queryFn: () => api.get<Vehiculo[]>('/logistica/vehiculos', { query: { empresaId } }),
  });
export const useChoferes = (empresaId: string) =>
  useQuery({
    queryKey: clavesLog.choferes(empresaId),
    queryFn: () => api.get<Chofer[]>('/logistica/choferes', { query: { empresaId } }),
  });
export const useRutas = (empresaId: string) =>
  useQuery({
    queryKey: clavesLog.rutas(empresaId),
    queryFn: () => api.get<Ruta[]>('/logistica/rutas', { query: { empresaId } }),
  });

/**
 * Una acción de la OD (POST con Idempotency-Key por intento). Al terminar refresca la OD, la lista,
 * los disponibles y el stock (la carga y la descarga mueven inventario).
 */
export function useAccionOd(empresaId: string, odId: string) {
  const qc = useQueryClient();
  const intentos = useIntentos();
  return useMutation({
    mutationFn: ({ ruta, cuerpo }: { ruta: string; cuerpo: Record<string, unknown> }) => {
      const c = { empresaId, ...cuerpo };
      return api.post<Record<string, unknown>>(`/logistica/ordenes/${odId}/${ruta}`, c, {
        idempotencia: intentos.clave(`od-${ruta}`, c),
      });
    },
    onSuccess: async () => {
      intentos.cerrar();
      await Promise.all([
        qc.invalidateQueries({ queryKey: clavesLog.logistica(empresaId) }),
        qc.invalidateQueries({ queryKey: ['emp', empresaId, 'stock'] }),
        qc.invalidateQueries({ queryKey: ['emp', empresaId, 'disponibilidad'] }),
      ]);
    },
    onError: (e) => intentos.cerrar(e),
  });
}
